import { useEffect, useState } from "react";
import {
  ApiRequestError,
  getLlmPrompt,
  updateLlmPromptOverride,
} from "../lib/client";
import type { LlmPrompt, LlmPromptOverride } from "../lib/types";
import { LLM_PROMPT_PURPOSE_LABELS } from "../lib/types";

type Status =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      override: LlmPromptOverride | null;
      defaultPrompt: LlmPrompt | null;
    };

/**
 * Show the constituent briefing prompt this subscriber uses, and write an
 * override.
 *
 * An override hides the product default. With no override, only the default
 * is shown. Saving always writes a subscriber override (and soft-deletes the
 * previous one).
 */
export function LlmPromptEditor({ subscriberId }: { subscriberId: string }) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: "loading" });
    setPrompt("");
    setStatus({ kind: "idle" });
    getLlmPrompt(subscriberId, "constituent_briefing")
      .then((view) => {
        if (cancelled) return;
        setLoad({
          kind: "ready",
          override: view.override,
          defaultPrompt: view.defaultPrompt,
        });
        setPrompt(view.override?.prompt ?? view.defaultPrompt?.prompt ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoad({
          kind: "error",
          message:
            err instanceof ApiRequestError
              ? err.message
              : "Failed to load the constituent briefing prompt.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [subscriberId]);

  const trimmed = prompt.trim();
  const hasOverride = load.kind === "ready" && load.override != null;
  const busy = status.kind === "saving" || load.kind === "loading";

  async function save() {
    if (!trimmed) return;
    setStatus({ kind: "saving" });
    try {
      const saved = await updateLlmPromptOverride(
        subscriberId,
        "constituent_briefing",
        trimmed,
      );
      setLoad((prev) =>
        prev.kind === "ready"
          ? { ...prev, override: saved }
          : {
              kind: "ready",
              override: saved,
              defaultPrompt: null,
            },
      );
      setPrompt(saved.prompt);
      setStatus({ kind: "saved" });
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to save the constituent briefing prompt.",
      });
    }
  }

  if (load.kind === "loading") {
    return <div className="panel">Loading prompt…</div>;
  }
  if (load.kind === "error") {
    return (
      <div className="panel">
        <span className="hint error">{load.message}</span>
      </div>
    );
  }

  return (
    <section className="panel">
      <h2 className="section-head">
        {LLM_PROMPT_PURPOSE_LABELS.constituent_briefing}
      </h2>
      <p className="hint">
        This subscriber uses either a per-org override or the product default —
        never both. Saving writes an override and soft-deletes the previous one.
      </p>

      <label className="check prompt-source-check">
        <input type="checkbox" checked={hasOverride} disabled />
        {hasOverride
          ? "Subscriber override is present"
          : "Using the product default (no override)"}
      </label>

      {hasOverride ? (
        <PromptBox
          source="override"
          prompt={prompt}
          disabled={busy}
          onChange={(value) => {
            setPrompt(value);
            if (status.kind === "saved" || status.kind === "error") {
              setStatus({ kind: "idle" });
            }
          }}
        />
      ) : (
        <PromptBox
          source="default"
          prompt={prompt}
          disabled={busy}
          missing={!load.defaultPrompt}
          onChange={(value) => {
            setPrompt(value);
            if (status.kind === "saved" || status.kind === "error") {
              setStatus({ kind: "idle" });
            }
          }}
        />
      )}

      <div className="toolbar">
        <button
          type="button"
          className="btn primary"
          onClick={save}
          disabled={busy || !trimmed}
        >
          {status.kind === "saving"
            ? "Saving…"
            : hasOverride
              ? "Save override"
              : "Save as override"}
        </button>
        {status.kind === "saved" && (
          <span className="hint ok-text">Saved.</span>
        )}
      </div>

      {status.kind === "error" && (
        <div className="banner error">{status.message}</div>
      )}
    </section>
  );
}

function PromptBox({
  source,
  prompt,
  disabled,
  missing,
  onChange,
}: {
  source: "override" | "default";
  prompt: string;
  disabled: boolean;
  missing?: boolean;
  onChange: (value: string) => void;
}) {
  const isOverride = source === "override";
  return (
    <div
      className={`prompt-source-box ${isOverride ? "is-override" : "is-default"}`}
    >
      <div className="prompt-source-head">
        <span className="prompt-source-label">
          {isOverride ? "Subscriber override" : "Product default"}
        </span>
        <span className="prompt-source-tag">
          {isOverride ? "Active for this org" : "Active (no override)"}
        </span>
      </div>
      {missing ? (
        <p className="hint">
          ps-main has no product default for this purpose. Paste a prompt and
          save to create a subscriber override.
        </p>
      ) : null}
      <label className="field">
        <span className="field-label">Prompt</span>
        <textarea
          className="json-editor prompt-editor"
          spellCheck
          placeholder={
            isOverride
              ? "Subscriber override prompt…"
              : "Product default prompt…"
          }
          value={prompt}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    </div>
  );
}
